/*
 * Purpose: Manage OTP state, requests, validation, cooldown, and channel selection.
 * Boundary: Hook only; owns state, side effects, and event handlers; no JSX.
 * Invariants:
 * - Cooldown starts at 30 seconds after request or resend.
 * - Validation triggers only on explicit call to validateOtp.
 * - Channel selection: local (+27) = [SMS, WhatsApp], international = [WhatsApp].
 * - Default channel is WhatsApp when available.
 * - External channel prop overrides internal channel selection.
 * - ASCII only; comments and strings must use plain ASCII.
 */

// Imports
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '../../toast/toast-slim';

// Constants
const COOLDOWN_SECONDS = 30;

// Channel-to-enum mapping for backend ChannelTypeEnum (SMS=1, Email=2, WhatsApp=3)
const CHANNEL_TYPE_MAP = { sms: 1, email: 2, whatsapp: 3 };

// Hook useOtp:
// Handles OTP entry, validation, cooldown timer, channel selection, and API requests
export function useOtp({ auth, idNumber, cellNumber, isProceedReadyOtp, otpLength, channel, resetSignal, onChange = () => { } } = {}) {
    // Get toastr from context
    const toastr = useToast();

    // States: OTP input and status
    const [otpValue, setOtpValue] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(0);
    const [hasRequested, setHasRequested] = useState(false);

    // Effect: Reset internal state when parent signals a reset (e.g. registration failure)
    useEffect(() => {
        if (resetSignal > 0) {
            setOtpValue('');
            setIsValid(false);
            setError('');
            setCooldown(0);
            setHasRequested(false);
        }
    }, [resetSignal]);

    // States: Internal channel selection (used when no external channel prop)
    const [internalSelectedChannel, setInternalSelectedChannel] = useState(null);

    // Derived: Locale from phone number prefix
    const isLocal = useMemo(() => {
        if (!cellNumber) return true;
        return cellNumber.startsWith('+27') || cellNumber.startsWith('27');
    }, [cellNumber]);

    // Derived: Available channels based on locale
    const availableChannels = useMemo(() => {
        return isLocal ? ['sms', 'whatsapp'] : ['whatsapp'];
    }, [isLocal]);

    // Effect: Set internal default channel when available channels change
    useEffect(() => {
        if (channel) return; // External channel prop takes precedence
        setInternalSelectedChannel(prev =>
            prev && availableChannels.includes(prev)
                ? prev
                : (availableChannels.includes('whatsapp') ? 'whatsapp' : availableChannels[0])
        );
    }, [availableChannels, channel]);

    // Derived: Effective channel — external prop overrides internal selection
    const effectiveChannel = channel || internalSelectedChannel;

    // Derived: Whether to show channel selector (2+ internal channels, no external override)
    const showChannelSelector = !channel && availableChannels.length >= 2;

    // Effect: Countdown timer during cooldown
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    // Effect: Push state changes to parent
    useEffect(() => {
        onChange({ isValid, otpValue, hasRequested, cooldown });
    }, [isValid, otpValue, hasRequested, cooldown, onChange]);

    // Handlers: OTP requests and resends
    const requestOtp = useCallback(async () => {
        if (!cellNumber) {
            setError('Missing cell number');
            return;
        }
        setError('');
        try {
            var reqDto = { channelAddress: cellNumber, idNumber: idNumber };
            if (effectiveChannel && CHANNEL_TYPE_MAP[effectiveChannel] != null) reqDto.channelType = CHANNEL_TYPE_MAP[effectiveChannel];

            var results = await auth.sendOneTimeCode(reqDto);
            if (results.isSuccess) {
                setHasRequested(true);
                setCooldown(COOLDOWN_SECONDS);
                toastr.success('Verification code sent');
            }
            else {
                toastr.error('Verification code request failed');
            }
        } catch (err) {
            toastr.error('Verification code request failed');
            setError('Request failed. Try again.');
        }
    }, [auth, idNumber, cellNumber, effectiveChannel]);

    const resendOtp = useCallback(async () => {
        if (cooldown > 0 || !cellNumber) return;
        setError('');
        try {
            var reqDto = { channelAddress: cellNumber, idNumber: idNumber };
            if (effectiveChannel && CHANNEL_TYPE_MAP[effectiveChannel] != null) reqDto.channelType = CHANNEL_TYPE_MAP[effectiveChannel];

            var results = await auth.resendOneTimeCode(reqDto);
            if (results.isSuccess) {
                setCooldown(COOLDOWN_SECONDS);
                toastr.success('Verification code sent');
            }
            else {
                toastr.error('Verification code request failed');
            }
        } catch (err) {
            toastr.error('Verification code request failed');
            setError('Resend failed. Try again.');
        }
    }, [auth, idNumber, cellNumber, cooldown, effectiveChannel]);

    // Handlers: Input and validation
    const onOtpChange = useCallback((value) => {
        const sanitized = value.replace(/[^0-9]/g, '').slice(0, otpLength);
        setOtpValue(sanitized);
        setError('');
        setIsValid(false);
    }, [otpLength]);

    const validateOtp = useCallback(async () => {
        if (!cellNumber) {
            setError('Missing cell number');
            setIsValid(false);
            return;
        }
        if (otpValue.length !== otpLength) {
            setError(`Please enter ${otpLength} characters`);
            setIsValid(false);
            return;
        }
        setError('');
        try {
            var reqDto = { channelAddress: cellNumber, oneTimeCode: otpValue, idNumber: idNumber };
            if (effectiveChannel && CHANNEL_TYPE_MAP[effectiveChannel] != null) reqDto.channelType = CHANNEL_TYPE_MAP[effectiveChannel];

            const results = await auth.validateOneTimeCode(reqDto);
            if (results.isSuccess) {
                setIsValid(true);
                setCooldown(0);
            }
            else {
                setError('Invalid OTP');
            }
        } catch (err) {
            setError('Validation failed. Try again.');
            setIsValid(false);
        }
    }, [auth, idNumber, cellNumber, otpValue, otpLength, effectiveChannel]);

    // Handler: Channel selection (for internal use)
    const setSelectedChannel = useCallback((ch) => {
        setInternalSelectedChannel(ch);
    }, []);

    // Return: Full state and handlers
    return {
        otpValue,
        onOtpChange,
        validateOtp,
        isValid,
        error,
        cooldown,
        hasRequested,
        requestOtp,
        resendOtp,
        idNumber,
        cellNumber,
        isProceedReadyOtp,
        otpLength,
        selectedChannel: effectiveChannel,
        availableChannels,
        showChannelSelector,
        setSelectedChannel,
    };
}
